import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Clock, Plus } from "lucide-react";

const AdminTimeSlotsPage = () => {
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newSlot, setNewSlot] = useState({
    name: "",
    start_time: "",
    end_time: "",
    slot_type: "fixed",
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchTimeSlots();
  }, []);

  const fetchTimeSlots = async () => {
    try {
      const response = await axios.get(`${API}/admin/timeslots`);
      setTimeSlots(response.data);
    } catch (error) {
      toast.error("Failed to load time slots");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlot = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${API}/admin/timeslots`, newSlot);
      toast.success("Time slot added successfully!");
      setShowAddDialog(false);
      setNewSlot({ name: "", start_time: "", end_time: "", slot_type: "fixed" });
      fetchTimeSlots();
    } catch (error) {
      toast.error("Failed to add time slot");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="admin-timeslots-page">
      <div className="pt-12 pb-12 px-6 max-w-7xl mx-auto">
        <Button
          data-testid="back-to-admin"
          onClick={() => navigate("/admin")}
          variant="ghost"
          className="mb-6 text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Admin
        </Button>

        <div className="fade-in">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#1A3C34] mb-4">
                Time Slot Configuration
              </h1>
              <p className="text-xl text-gray-600">Configure booking time slots for users.</p>
            </div>

            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-timeslot-btn"
                  className="bg-[#FF6B35] hover:bg-[#E55A28] text-white rounded-full px-6 py-6"
                >
                  <Plus className="mr-2" size={20} />
                  Add Time Slot
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-[#1A3C34]">
                    Add New Time Slot
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddSlot} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-medium">
                      Slot Name
                    </Label>
                    <Input
                      id="name"
                      data-testid="timeslot-name"
                      placeholder="Morning Shift"
                      value={newSlot.name}
                      onChange={(e) => setNewSlot({ ...newSlot, name: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="start_time" className="text-gray-700 font-medium">
                      Start Time
                    </Label>
                    <Input
                      id="start_time"
                      data-testid="timeslot-start"
                      type="time"
                      value={newSlot.start_time}
                      onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="end_time" className="text-gray-700 font-medium">
                      End Time
                    </Label>
                    <Input
                      id="end_time"
                      data-testid="timeslot-end"
                      type="time"
                      value={newSlot.end_time}
                      onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="slot_type" className="text-gray-700 font-medium">
                      Slot Type
                    </Label>
                    <Select
                      value={newSlot.slot_type}
                      onValueChange={(value) => setNewSlot({ ...newSlot, slot_type: value })}
                    >
                      <SelectTrigger
                        data-testid="timeslot-type"
                        className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed</SelectItem>
                        <SelectItem value="flexible">Flexible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    data-testid="submit-timeslot"
                    type="submit"
                    className="w-full bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full py-6"
                  >
                    Add Time Slot
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="p-6 bg-[#1A3C34] text-white border-none">
              <p className="text-sm opacity-90 mb-2">Total Time Slots</p>
              <p className="text-4xl font-bold">{timeSlots.length}</p>
            </Card>
            <Card className="p-6 bg-[#F5F2EB] border-none">
              <p className="text-sm text-gray-600 mb-2">Fixed Slots</p>
              <p className="text-4xl font-bold text-[#1A3C34]">
                {timeSlots.filter((s) => s.slot_type === "fixed").length}
              </p>
            </Card>
            <Card className="p-6 bg-[#FF6B35] text-white border-none">
              <p className="text-sm opacity-90 mb-2">Flexible Slots</p>
              <p className="text-4xl font-bold">
                {timeSlots.filter((s) => s.slot_type === "flexible").length}
              </p>
            </Card>
          </div>

          {/* Time Slots Grid */}
          {timeSlots.length === 0 ? (
            <Card className="p-12 text-center">
              <Clock className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-gray-600 text-lg mb-4">No time slots configured</p>
              <Button
                data-testid="add-first-timeslot"
                onClick={() => setShowAddDialog(true)}
                className="bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full px-6"
              >
                <Plus className="mr-2" size={20} />
                Add Your First Time Slot
              </Button>
            </Card>
          ) : (
            <Card className="p-6 border border-gray-100">
              <h2 className="text-2xl font-bold text-[#1A3C34] mb-6">All Time Slots</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {timeSlots.map((slot) => (
                  <div
                    key={slot._id}
                    data-testid={`timeslot-${slot._id}`}
                    className="p-6 bg-white border-2 border-gray-100 rounded-xl hover:shadow-xl transition-all"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-[#1A3C34] rounded-xl flex items-center justify-center">
                        <Clock className="text-white" size={24} />
                      </div>
                      <div
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          slot.slot_type === "fixed"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-purple-100 text-purple-800"
                        }`}
                      >
                        {slot.slot_type}
                      </div>
                    </div>

                    <h3 className="text-2xl font-bold text-[#1A3C34] mb-4">{slot.name}</h3>

                    <div className="space-y-2">
                      <p className="text-gray-600">
                        <span className="font-medium">Start:</span> {slot.start_time}
                      </p>
                      <p className="text-gray-600">
                        <span className="font-medium">End:</span> {slot.end_time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTimeSlotsPage;